import {
  namedOperations,
  BuildGroup_Add_AsyncResultQuery,
  BuildGroup_Add_AsyncResultDocument,
  Settings_ChangeCoordinatorPort_AsyncResultQuery,
  Settings_ChangeCoordinatorPort_AsyncResultDocument,
  Settings_Update_AsyncResultQuery,
  Settings_Update_AsyncResultDocument,
  Agents_BuildCache_Update_AsyncResultDocument,
  Agents_BuildCache_Update_AsyncResultQuery,
  Agents_Action_AsyncResultQuery,
  Agents_Action_AsyncResultDocument,
  Settings_CheckPort_AsyncResultDocument,
  Settings_CheckPort_AsyncResultQuery,
  License_Action_AsyncResultDocument,
  License_Action_AsyncResultQuery,
  License_RegisterAgents_AsyncResultQuery,
  License_RegisterAgents_AsyncResultDocument,
  License_RegisterBuildCache_AsyncResultQuery,
  License_RegisterBuildCache_AsyncResultDocument,
  License_Action_Generate_AsyncResultDocument,
  License_Action_Generate_AsyncResultQuery,
  Cloud_Action_AsyncResultQuery,
  Cloud_Action_AsyncResultDocument,
  BuildGroup_Delete_AsyncResultDocument,
  BuildGroup_Delete_AsyncResultQuery,
  BuildGroup_Edit_AsyncResultDocument,
  BuildGroup_Edit_AsyncResultQuery,
  Cloud_Action_Wakeup_AsyncResultQuery,
  Cloud_Action_Wakeup_AsyncResultDocument,
} from '@/graphql/codegen/generated/graphql';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { ApolloQueryResult, DocumentNode } from '@apollo/client/core';
import sleep from 'sleep-promise';

const MAX_RETRIES = 200;
const LONG_POLLING_TIMEOUT = 300;
type PossibleQueryType = keyof Pick<
  typeof namedOperations.Query,
  | 'settings_checkPort_asyncResult'
  | 'settings_changeCoordinatorPort_asyncResult'
  | 'license_registerAgents_asyncResult'
  | 'settings_update_asyncResult'
  | 'buildGroup_add_asyncResult'
  | 'buildGroup_delete_asyncResult'
  | 'buildGroup_edit_asyncResult'
  | 'agents_action_asyncResult'
  | 'license_action_asyncResult'
  | 'license_action_generate_asyncResult'
  | 'license_registerBuildCache_asyncResult'
  | 'agents_buildCache_update_asyncResult'
  | 'cloud_action_asyncResult'
  | 'cloud_action_wakeup_asyncResult'
>;
type IQueryResult = {
  license_registerBuildCache_asyncResult: ApolloQueryResult<License_RegisterBuildCache_AsyncResultQuery>;
  license_registerAgents_asyncResult: ApolloQueryResult<License_RegisterAgents_AsyncResultQuery>;
  settings_update_asyncResult: ApolloQueryResult<Settings_Update_AsyncResultQuery>;
  settings_changeCoordinatorPort_asyncResult: ApolloQueryResult<Settings_ChangeCoordinatorPort_AsyncResultQuery>;
  buildGroup_add_asyncResult: ApolloQueryResult<BuildGroup_Add_AsyncResultQuery>;
  buildGroup_delete_asyncResult: ApolloQueryResult<BuildGroup_Delete_AsyncResultQuery>;
  buildGroup_edit_asyncResult: ApolloQueryResult<BuildGroup_Edit_AsyncResultQuery>;
  agents_buildCache_update_asyncResult: ApolloQueryResult<Agents_BuildCache_Update_AsyncResultQuery>;
  agents_action_asyncResult: ApolloQueryResult<Agents_Action_AsyncResultQuery>;
  license_action_asyncResult: ApolloQueryResult<License_Action_AsyncResultQuery>;
  license_action_generate_asyncResult: ApolloQueryResult<License_Action_Generate_AsyncResultQuery>;
  settings_checkPort_asyncResult: ApolloQueryResult<Settings_CheckPort_AsyncResultQuery>;
  cloud_action_asyncResult: ApolloQueryResult<Cloud_Action_AsyncResultQuery>;
  cloud_action_wakeup_asyncResult: ApolloQueryResult<Cloud_Action_Wakeup_AsyncResultQuery>;
};
const DOCUMENT_MAP: Record<PossibleQueryType, DocumentNode> = {
  license_registerBuildCache_asyncResult: License_RegisterBuildCache_AsyncResultDocument,
  license_registerAgents_asyncResult: License_RegisterAgents_AsyncResultDocument,
  settings_update_asyncResult: Settings_Update_AsyncResultDocument,
  settings_changeCoordinatorPort_asyncResult: Settings_ChangeCoordinatorPort_AsyncResultDocument,
  buildGroup_add_asyncResult: BuildGroup_Add_AsyncResultDocument,
  buildGroup_delete_asyncResult: BuildGroup_Delete_AsyncResultDocument,
  buildGroup_edit_asyncResult: BuildGroup_Edit_AsyncResultDocument,
  agents_buildCache_update_asyncResult: Agents_BuildCache_Update_AsyncResultDocument,
  agents_action_asyncResult: Agents_Action_AsyncResultDocument,
  settings_checkPort_asyncResult: Settings_CheckPort_AsyncResultDocument,
  license_action_asyncResult: License_Action_AsyncResultDocument,
  license_action_generate_asyncResult: License_Action_Generate_AsyncResultDocument,
  cloud_action_asyncResult: Cloud_Action_AsyncResultDocument,
  cloud_action_wakeup_asyncResult: Cloud_Action_Wakeup_AsyncResultDocument,
};
class LongPolling {
  #runningQueries: Set<string>;

  constructor() {
    this.#runningQueries = new Set();
  }

  public abort(id: string) {
    this.#runningQueries.delete(id);
  }

  public async fetch<T extends PossibleQueryType>(queryName: T, id: string): Promise<IQueryResult[T]> {
    return new Promise((resolve, reject) => {
      let retry = 0;
      this.#runningQueries.add(id);
      const getStatus: () => Promise<void | IQueryResult[T]> = async () => {
        if (retry++ > MAX_RETRIES) return reject(new Error(`LongPolling timeout - query: ${queryName} ,id: ${id}`));
        if (!this.#runningQueries.has(id)) return reject(new Error(`LongPolling aborted - query: ${queryName} ,id: ${id}`));
        const result: IQueryResult[T] = await apolloClient.query({
          query: DOCUMENT_MAP[queryName],
          variables: {
            id,
          },
          fetchPolicy: 'no-cache',
        });
        if ((result?.data as any)[queryName].result) {
          this.#runningQueries.delete(id);
          return resolve(result);
        }
        await sleep(LONG_POLLING_TIMEOUT);
        return getStatus();
      };
      getStatus();
    });
  }
}
const longPolling = new LongPolling();
export { longPolling };
